import { useNewCode, useVariant } from '@calendly/feature-check-react';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useEnforceUpgrade } from 'components/billing/useEnforceUpgrade';
import { AINotetakerButton } from 'components/meeting_intelligence_awareness';
import { ORGANIZATION_INVITATION_PERMISSIONS } from 'constants/organization_invitation_permissions';
import { NoSeatsRemainingModal, isMIFeatureAvailableSelector } from 'features/billing';
import { PretendModeHeader } from 'features/header/pretend_mode_header';
import { AddUsersModal, addUsersModalOpenSelector, openAddUserModal } from 'features/invite_flow';
import { useIsWideMainLayout } from 'features/layout';
import { DetachedNavigationTrigger, useLeftNavigationState } from 'features/left_navigation';
import { MR_ONBOARDING_WIZARD, showAcknowledgementModal, useGetMeetingIntelligenceUserSettingsQuery, useEnableNotetakerWithLoader, useNotetakerFreeMeetingsQuota } from 'features/meeting_intelligence';
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants';
import { useTrack } from 'hooks/analytics_events/useTrack';
import { useMeetingIntelligenceAwarenessEnabled } from 'hooks/meeting_intelligence_awareness';
import { useExtensionUsageData } from 'hooks/useExtensionUsageData';
import { useHandleFirstTimeFiveFreeRecapsExposure } from 'hooks/useHandleFirstTimeFiveFreeRecapsExposure';
import { useIsExtensionInstalled } from 'hooks/useIsExtensionInstalled';
import { useNotetakerFiveFreeRecapsExperiment } from 'hooks/useNotetakerFiveFreeRecapsExperiment';
import { useNotetakerFreeExperiment } from 'hooks/useNotetakerFreeExperiment';
import { useTriggerSeatsPaywall } from 'hooks/useTriggerSeatsPaywall';
import { orgInvitationPermissionsSelector } from 'selectors/current_organization';
import { createdAtSelector, eventsCountSelector, isAdminOrOwnerSelector, isOnlyGroupAdminSelector, localeSelector } from 'selectors/current_user';
import Cookies from 'services/cookie';
import { isUnsupportedBrowser } from 'shared/browsers/helpers';
import { ExtensionDownloadCta } from './extension_download_cta';
import { ExtensionReengageCta, variants } from './extension_reengage_cta';
import { InviteUserButton } from './invite_user_button';
import { UserMenu } from './user_menu';
const stickyContainerIsPhoneCss = "s18hp9n4";
const stickyContainerIsPretendingCss = "s1thxgno";
const stickyContainerCss = "s46ptfg";
const StickyContainer = forwardRef((props, ref) => {
  const {
    isPhone,
    isPretending,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(stickyContainerCss, {
    [stickyContainerIsPhoneCss]: isPhone,
    [stickyContainerIsPretendingCss]: isPretending
  }, props.className)} />;
});
const containerCss = "cxbuhmt";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'header';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const innerContainerIsPhoneCss = "i4s70e3";
const innerContainerIsWideCss = "i1am4iwa";
const innerContainerCss = "iuicuzf";
const InnerContainer = forwardRef((props, ref) => {
  const {
    isPhone,
    isWide,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(innerContainerCss, {
    [innerContainerIsPhoneCss]: isPhone,
    [innerContainerIsWideCss]: isWide
  }, props.className)} />;
});
const globalActionsCss = "g1g7o2f4";
const GlobalActions = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(globalActionsCss, props.className)} />;
});
const navigationWrapperCss = "nc62lzr";
const NavigationWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(navigationWrapperCss, props.className)} />;
});
function getRandomVariant() {
  const keys = Object.keys(variants);
  const randomIndex = Math.floor(Math.random() * keys.length);
  return variants[keys[randomIndex]];
}
const reengageCtaProps = getRandomVariant();
const inactivityCheckWindow = {
  variant_1: 30,
  // days
  variant_2: 15 // days
};
export const Header = ({
  isPretending
}) => {
  const dispatch = useDispatch();
  const totalSeatsRef = useRef();
  const {
    pathname
  } = useLocation();
  const track = useTrack();
  const cookies = new Cookies();
  const isPhone = useDeviceType('isPhone');
  const isDesktopUp = useDeviceType('isDesktopUp');
  const leftNavigationState = useLeftNavigationState();
  const isGrowthReleaseBundleFlagEnabled = useNewCode('clients_q2_growth_release_bundle')?.enabled;
  const isExtensionPromoFlagEnabled = useNewCode('clients_web_app_promotion_feature')?.enabled;
  const extensionInstallCtaTimingVariant = useVariant('clients_install_cta_timing_experiment', true);
  const reengageCtaTimingExperimentVariant = useVariant('clients_reengage_cta_timing_experiment', true);
  const isActivationOnboardingExperimentEnabled = useNewCode('activation_q3')?.enabled;
  const isExtensionInstalled = useIsExtensionInstalled();
  const extensionUsageData = useExtensionUsageData();
  const [isWide] = useIsWideMainLayout();
  const enforceUpgrade = useEnforceUpgrade('meeting_intelligence');
  const enableNotetaker = useEnableNotetakerWithLoader();
  const {
    isInExperiment: isInNotetakerFreeExperiment
  } = useNotetakerFreeExperiment();
  const userLocale = useSelector(localeSelector);
  const eventsCount = useSelector(eventsCountSelector);
  const userCreatedAt = useSelector(createdAtSelector);
  const isAdminOrOwner = useSelector(isAdminOrOwnerSelector);
  const isOnlyGroupAdmin = useSelector(isOnlyGroupAdminSelector);
  const isAddUsersModalOpen = useSelector(addUsersModalOpenSelector);
  const orgInvitationPermissions = useSelector(orgInvitationPermissionsSelector);
  const [noSeatsRemainingModalOpen, setNoSeatsRemainingModalOpen] = useState(false);
  const {
    data: meetingIntelligenceUserData
  } = useGetMeetingIntelligenceUserSettingsQuery();
  const isMIBillingFeatureAvailable = useSelector(isMIFeatureAvailableSelector);
  const mIAwarenessEnabled = useMeetingIntelligenceAwarenessEnabled();
  const hasNotetakerEnabled = !!meetingIntelligenceUserData?.miEnabled && !!isMIBillingFeatureAvailable;
  const {
    isInExperiment: isInNotetakerFiveFreeRecapsExperiment
  } = useNotetakerFiveFreeRecapsExperiment(mIAwarenessEnabled && !hasNotetakerEnabled);
  // For 5 free recaps experiment, handle first-time exposure to enable notetaker with no meeting tracking
  useHandleFirstTimeFiveFreeRecapsExposure(isInNotetakerFiveFreeRecapsExperiment);
  const {
    hasRemainingFreeMeetings
  } = useNotetakerFreeMeetingsQuota({
    skip: !isInNotetakerFiveFreeRecapsExperiment
  });
  const shouldShowNotetakerButton = !meetingIntelligenceUserData?.miEnabled || !isMIBillingFeatureAvailable && !hasRemainingFreeMeetings;
  const leftNavPhone = leftNavigationState === 'phone-collapsed' || leftNavigationState === 'phone-expanded';
  const canInviteAsGroupAdmin = isOnlyGroupAdmin && [ORGANIZATION_INVITATION_PERMISSIONS.GROUP_ADMIN, ORGANIZATION_INVITATION_PERMISSIONS.USER].includes(orgInvitationPermissions);
  const canInviteAsEndUser = !isAdminOrOwner && !isOnlyGroupAdmin && orgInvitationPermissions === ORGANIZATION_INVITATION_PERMISSIONS.USER;
  const canManageInvitation = isAdminOrOwner || canInviteAsGroupAdmin || canInviteAsEndUser;
  const userCreatedAtDaysDiff = useMemo(() => {
    const createdAtDate = moment(userCreatedAt);
    if (!createdAtDate.isValid()) {
      return false;
    }
    const today = moment();
    const differenceInDays = today.diff(createdAtDate, 'days');
    return differenceInDays;
  }, [userCreatedAt]);
  const showExtensionPromo = () => {
    if (isExtensionInstalled !== false) return false;
    if (!isDesktopUp) return false;
    if (!isExtensionPromoFlagEnabled) return false;
    if (!isGrowthReleaseBundleFlagEnabled) return false;
    if (userLocale !== 'en') return false;
    if (isUnsupportedBrowser()) return false;
    if (eventsCount < 5) return false;
    return true;
  };
  const showExtensionInstallCtaExperiment = () => {
    if (isExtensionInstalled !== false) return false;
    if (!isDesktopUp) return false;
    if (userLocale !== 'en') return false;
    if (isUnsupportedBrowser()) return false;

    // Control
    if (extensionInstallCtaTimingVariant === 'control' && eventsCount >= 5 && userCreatedAtDaysDiff <= 14) return true;

    // Variant 1
    if (extensionInstallCtaTimingVariant === 'variant_1' && eventsCount >= 1) return true;

    // Variant 2
    if (extensionInstallCtaTimingVariant === 'variant_2' && userCreatedAtDaysDiff >= 1) return true;
    return false;
  };
  const userUsedExtensionInThePast = numOfDays => {
    if (extensionUsageData.loading || extensionUsageData.error) {
      return false;
    }
    const usageData = extensionUsageData?.data;
    if (!usageData?.userAuthTime) return false; // Has not used extension if not logged in, regardless of open time
    if (!usageData?.sidebarOpenTime) return false;
    const now = moment();
    const sidebarOpenTime = moment.unix(Number(usageData.sidebarOpenTime));
    const durationSinceLastOpen = now.diff(sidebarOpenTime, 'days', true);
    if (durationSinceLastOpen > numOfDays) return false;
    return true;
  };
  const showReengagePromo = () => {
    if (isExtensionInstalled !== true) return false;
    if (reengageCtaTimingExperimentVariant !== 'variant_1' && reengageCtaTimingExperimentVariant !== 'variant_2') return false;
    if (!isDesktopUp) return false;
    if (userLocale !== 'en') return false;
    if (isUnsupportedBrowser()) return false;
    const inactivityCheckDays = inactivityCheckWindow[reengageCtaTimingExperimentVariant];
    if (userUsedExtensionInThePast(inactivityCheckDays)) return false;
    return true;
  };
  const openModal = () => {
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        modal_name: 'invite_user_email_flow',
        action: 'opening',
        path: pathname
      }
    });
    dispatch(openAddUserModal());
  };
  const [triggerPaywall, triggerState, resetTrigger] = useTriggerSeatsPaywall({
    onInviteOpen: openModal,
    onNoSeatsOpen: () => setNoSeatsRemainingModalOpen(true),
    totalSeatsRef
  });
  useEffect(() => {
    if (!triggerState || triggerState === 'invite-modal-open') return;
    if (triggerState !== 'upgrade-modal-open') {
      track({
        name: TRACKING_EVENT_NAMES.PAYWALL_SHOWN,
        entities: ['attribution', 'organization', 'user'],
        properties: {
          paywall_feature: 'invite_flow_add_seats',
          paywall_type: 'popup',
          plan_required: 'standard'
        }
      });
    }
    resetTrigger();
  }, [triggerState, resetTrigger, track]);
  const handleInviteUserClick = () => isActivationOnboardingExperimentEnabled ? triggerPaywall() : openModal();

  // https://calendlyapp.atlassian.net/browse/APP-10607
  // If the user entered through the mobile web entry point
  // we should hide the header so that they can't navigate
  // around and run into paywalls. This is preventing certification
  // for the app stores.
  if (cookies.getCookie('mobile_webview') === 'true') {
    return null;
  }
  const handleNotetakerButtonClick = () => {
    // Prevent modal from showing if admin has disabled notetaker for the organization
    if (meetingIntelligenceUserData?.adminDisabledNotetaker) {
      return;
    }
    track({
      name: TRACKING_EVENT_NAMES.CTA_CLICKED,
      entities: ['attribution', 'organization', 'user'],
      properties: {
        cta_key: 'ai_notetaker_global_header_entry_point',
        cta_text: 'try_notetaker_for_free',
        cta_destination: 'ai_notetaker_setup_flow',
        action: 'ai_notetaker_free_trial_started'
      }
    });

    // For users in the experiment, always open the onboarding wizard
    if (isInNotetakerFreeExperiment) {
      dispatch(showAcknowledgementModal(MR_ONBOARDING_WIZARD.USER_TRIGGERED));
    } else if (hasRemainingFreeMeetings) {
      enableNotetaker();
    } else {
      enforceUpgrade(enableNotetaker);
    }
  };
  return <>
      <PretendModeHeader />
      <StickyContainer isPretending={isPretending} isPhone={isPhone}>
        <Container>
          <AddUsersModal isOpen={isAddUsersModalOpen} />
          <NoSeatsRemainingModal isOpen={noSeatsRemainingModalOpen} onClose={() => setNoSeatsRemainingModalOpen(false)} totalSeats={totalSeatsRef.current} />
          {isPhone && leftNavPhone && <NavigationWrapper>
              <DetachedNavigationTrigger showExpanded={false} />
            </NavigationWrapper>}
          <InnerContainer isPhone={isPhone} isWide={isWide}>
            <GlobalActions>
              {showExtensionPromo() && <ExtensionDownloadCta promo />}
              {showExtensionInstallCtaExperiment() && <ExtensionDownloadCta />}
              {showReengagePromo() && <ExtensionReengageCta {...reengageCtaProps} />}
              {mIAwarenessEnabled && shouldShowNotetakerButton && <AINotetakerButton onClick={handleNotetakerButtonClick} location="global_app_header" />}
              {canManageInvitation && <InviteUserButton onClick={handleInviteUserClick} />}
              <UserMenu />
            </GlobalActions>
          </InnerContainer>
        </Container>
      </StickyContainer>
    </>;
};

require("./header.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./header.js");